import Button from '@components/std-components/Button'
import Typography from '@components/std-components/Typography'
import { NoRecordFound } from '@components/UI-Components/NoRecordFound'
import { GeneralSelectionStore } from '@stores/SelectionStore'
import map from 'lodash/map'
import { useObserver } from 'mobx-react-lite'
import React, { ElementType, FC, useCallback, useEffect, useMemo } from 'react'
import List, { ListProps as ListElement } from './components/List'
import ListItem, { ListItemProps as ListItemElement } from './components/ListItem'
import SelectAllElement, {
  SelectAllElementProps as SelectAllElementItem
} from './components/SelectAllElement'
import SelectWrapper, {
  SelectWrapperProps as SelectWrapperElement
} from './components/SelectWrapper'
import { SelectionContext } from './contexts/store-context'
import * as css from './SelectComponent.css'

export interface SelectComponentProps {
  options: any[]
  selected: any[]
  multiple?: boolean
  onSelect?: ({ selected }: { selected: any[] }) => void
  title?: string
  selectLabel?: string
  confirmLabel?: string
  allowSelectAll?: boolean
  allowDeselectAll?: boolean
  disabledOptions?: any[]
  disabled?: boolean
  selectAllElementTemplate?: ElementType<SelectAllElementItem>
  listElementTemplate?: ElementType<ListElement>
  itemTemplate?: ElementType<ListItemElement>
  selectWrapperTemplate?: ElementType<SelectWrapperElement>
  // @deprecated : Remove in favor of listItemTemplate
  listClassName?: string
  tabComponent?: any
  columnsList?: any
  filterSubColumns?: boolean
  scrollIntoView?: any
  isScrollable?: boolean
  noRecordClassName?: string
}

export const SelectComponent: FC<SelectComponentProps> = ({
  options,
  selected,
  onSelect,
  title,
  selectLabel,
  allowDeselectAll,
  confirmLabel,
  allowSelectAll,
  disabledOptions,
  disabled,
  listClassName,
  noRecordClassName,
  selectAllElementTemplate: SelectAllElementCmp = SelectAllElement,
  listElementTemplate: ListCmp = List,
  itemTemplate: ItemCmp = ListItem,
  selectWrapperTemplate: WrapperCmp = SelectWrapper,
  multiple = true,
  tabComponent,
  filterSubColumns = false,
  scrollIntoView,
  isScrollable
}) => {
  return useObserver(function useHook() {
    const selectContextValue = useMemo(
      () => ({ selectionStore: new GeneralSelectionStore() }),
      []
    )
    const {
      selectionStore: {
        isAllSelected,
        selectAll,
        deselectAll,
        setData,
        setSelectedRows,
        setDisabledRows,
        isSelected,
        toggle,
        selected: selectedRows,
        disabled: disabledRows
      }
    } = selectContextValue
    useEffect(() => {
      setData(options)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options])
    useEffect(() => {
      setSelectedRows(selected)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected])
    useEffect(() => {
      setDisabledRows(disabledOptions as any[])
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disabledOptions])

    const tempDisabled = disabledOptions
    const isOptionDisabled = useCallback(
      (option): boolean => {
        return tempDisabled?.includes(option)
      },
      [tempDisabled]
    )

    const ListItemComponent = React.memo(props => {
      return (
        <ListCmp listClassName={listClassName}>
          {map(options, (option, index) => (
            <div key={option} className={css.option}>
              <ItemCmp
                index={Number(index)}
                isSelected={isSelected(option)}
                onSelect={() => {
                  if (!multiple) {
                    deselectAll()
                  }
                  toggle(option)
                  !confirmLabel &&
                    onSelect({
                      selected: Array.from(selectedRows)
                    })
                  scrollIntoView && scrollIntoView()
                }}
                option={option}
                onDeselect={() => {
                  if (!multiple) {
                    deselectAll()
                  }
                  toggle(option)
                  !confirmLabel &&
                    onSelect({
                      selected: Array.from(selectedRows)
                    })
                  scrollIntoView && scrollIntoView()
                }}
                disabled={disabled || isOptionDisabled(option)}
              />
            </div>
          ))}
        </ListCmp>
      )
    })

    return (
      <SelectionContext.Provider value={selectContextValue}>
        {title && !filterSubColumns && (
          <div className={css.selectionHeader}>
            <Typography variant='subHeading3' color='primary'>
              {title}
            </Typography>
          </div>
        )}
        {options?.length ? (
          <WrapperCmp isScrollable={isScrollable}>
            {allowSelectAll && (
              <div className={css.selectAll}>
                <SelectAllElementCmp
                  filterSubColumns={filterSubColumns}
                  subTitle={title}
                  isSelected={isAllSelected}
                  onSelect={() => {
                    selectAll(options)
                    onSelect({
                      selected: Array.from(selectedRows)
                    })
                    scrollIntoView && scrollIntoView()
                  }}
                  onDeselect={() => {
                    deselectAll(options)
                    onSelect({
                      selected: Array.from(selectedRows)
                    })
                    scrollIntoView && scrollIntoView()
                  }}
                  disabled={disabled}
                />
              </div>
            )}
            {tabComponent ? tabComponent : null}
            <ListItemComponent />
          </WrapperCmp>
        ) : (
          <NoRecordFound className={noRecordClassName} />
        )}
        {confirmLabel && (
          <div className={css.confirmButtonWrapper}>
            <Button
              variant='primary'
              theme='custom'
              type='button'
              className={css.confirmButton}
              disabled={
                !options?.length || disabled || !selectedRows.size || !allowDeselectAll
                  ? selectedRows.size > 0 &&
                    selectedRows.size === disabledRows.size &&
                    isAllSelected
                  : false
              }
              onClick={() => {
                onSelect({ selected: Array.from(selectedRows) })
              }}
            >
              <Typography variant='custom'>{confirmLabel}</Typography>
            </Button>
          </div>
        )}
      </SelectionContext.Provider>
    )
  })
}
